import { useState, useMemo, useEffect, memo } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Image } from '@/components/Analysis/shared/Image';
import { FileUploadStatus, ImageType, PointOfInterest } from '@/__generated__/graphql';
import { ImagePreviewDialog } from './ImagePreviewDialog/ImagePreviewDialog';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useSetAtom } from 'jotai';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import {
  PreviewContainer,
  PreviewImage,
  SliderNavButton,
  DotsContainer,
  DotIndicator,
  EmptyPlaceholder,
} from './styles';
import { useRouter, useInsightsPageParams } from '@/hooks';

interface PreviewProps {
  data?: PointOfInterest;
  poiIndex?: number;
}

export const Preview = memo(function Preview({ data, poiIndex }: PreviewProps) {
  const primaryImage = data?.files?.find((file) => file.imageType === ImageType.Primary);
  const router = useRouter();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);
  const { setPoi, clearPoi } = useInsightsPageParams();

  const images = useMemo(() => {
    const imageList = [];

    // Add a fallback till previous images are synced
    if (!primaryImage && data?.screenshotPaths) {
      const primaryImageSrc = data?.screenshotPaths?.viewpoint
        ? getCloudfrontUrl(data.screenshotPaths.viewpoint)
        : null;

      if (primaryImageSrc) {
        imageList.push({
          src: primaryImageSrc,
          id: 'primary',
          name: data?.location?.name || 'Primary Image',
          type: 'primary',
        });
      }
    }

    if (primaryImage) {
      imageList.push({
        src: getCloudfrontUrl(primaryImage.resourcePath),
        id: 'primary',
        name: 'Primary Image',
        type: 'primary',
      });
    }
    if (data?.files) {
      const attachmentImages = data?.files
        .filter(
          (file) =>
            file.resourcePath &&
            file.uploadStatus === FileUploadStatus.Success &&
            file.imageType !== ImageType.WhiteBgPdf &&
            file.type !== 'PDF'
        )
        .map((file) => ({
          src: getCloudfrontUrl(file.resourcePath),
          id: file.resourcePath,
          name: file.name || 'Attachment',
          type: 'attachment',
        }));

      imageList.push(...attachmentImages);
    }

    return imageList;
  }, [data?.files, data?.location?.name, data?.screenshotPaths, primaryImage]);

  const currentImage = useMemo(
    () => images[currentImageIndex] || images[0],
    [images, currentImageIndex]
  );

  const imageSrc = useMemo(
    () => currentImage?.src || '/assets/reporting-spherical-view.png',
    [currentImage?.src]
  );

  const imageAlt = useMemo(() => currentImage?.name || 'POI Preview', [currentImage?.name]);

  const MemoizedImageContent = useMemo(
    () => (
      <Image
        key={imageSrc}
        src={imageSrc}
        alt={imageAlt}
        imgSx={{
          width: '25.6rem',
          height: '16.9rem',
          objectFit: 'cover',
          borderRadius: '0.8rem',
          overflow: 'hidden',
          transition: 'opacity 0s ease-in-out',
        }}
      />
    ),
    [imageSrc, imageAlt]
  );

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  const handleOpen = () => {
    if (data?.id) {
      setSelectedPointOfInterestId(data.id);
      if (router.query?.poi !== data.id) setPoi(data.id);
      else setDialogOpen(true);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedPointOfInterestId(undefined);
    clearPoi();
  };

  useEffect(() => {
    const { poi, poiName } = router.query || {};
    if ((poi && data?.id === poi) || (poiName && data?.name === poiName)) {
      if (data?.id) setSelectedPointOfInterestId(data.id);
      setDialogOpen(true);
    }
  }, [router.query, data, setSelectedPointOfInterestId]);

  return (
    <>
      <PreviewContainer onClick={handleOpen}>
        <PreviewImage
          className={`review-panel-poi-preview-image review-panel-${data?.type
            ?.replaceAll('_', '-')
            .toLowerCase()}-poi-preview-image`}
        >
          {!primaryImage && images.length === 0 ? (
            <EmptyPlaceholder
              sx={{
                width: '25.6rem',
                height: '16.9rem',
              }}
            >
              Primary image not available
            </EmptyPlaceholder>
          ) : (
            MemoizedImageContent
          )}
        </PreviewImage>

        {images.length > 1 && (
          <>
            <SliderNavButton
              id="image-preview-slider-previous-button"
              className="slider-controls"
              onClick={handlePrevious}
              sx={{ left: '8px' }}
            >
              <ChevronLeft />
            </SliderNavButton>

            <SliderNavButton
              id="image-preview-slider-next-button"
              className="slider-controls"
              onClick={handleNext}
              sx={{ right: '8px' }}
            >
              <ChevronRight />
            </SliderNavButton>
          </>
        )}

        {images.length > 1 && (
          <DotsContainer>
            {images.map((_, index) => (
              <DotIndicator
                key={index}
                isActive={index === currentImageIndex}
                onClick={(e) => handleDotClick(index, e)}
              />
            ))}
          </DotsContainer>
        )}
      </PreviewContainer>

      {dialogOpen && (
        <ImagePreviewDialog
          open={dialogOpen}
          onClose={handleClose}
          currentImageIndex={currentImageIndex}
          onChangeImage={setCurrentImageIndex}
          poiIndex={poiIndex}
        />
      )}
    </>
  );
});
